'use client';

import React, { useRef } from 'react';

import type { DropdownMenuProps } from '@radix-ui/react-dropdown-menu';

import { useEditorRef } from '@udecode/plate/react';
import { ArrowUpToLineIcon } from 'lucide-react';

import {
  importFromDocx,
  importFromOdt,
  importFromHtml,
  importFromMarkdown,
  importFromText
} from '@/services/import/document-import';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
  useOpenState,
} from './dropdown-menu';
import { ToolbarButton } from './toolbar';

export function ChapterImportToolbarButton({ children, ...props }: DropdownMenuProps) {
  const editor = useEditorRef();
  const openState = useOpenState();

  // Handle file import using the import service
  const handleFileImport = async (file: File, format: string) => {
    try {
      let nodes: any[] = [];

      switch (format) {
        case 'docx':
          nodes = await importFromDocx(file, { preserveFormatting: true });
          break;

        case 'odt':
          nodes = await importFromOdt(file, { convertHeadings: true });
          break;

        case 'html':
          nodes = await importFromHtml(file, { preserveFormatting: true });
          break;

        case 'markdown':
          nodes = await importFromMarkdown(file, { preserveFormatting: false });
          break;

        case 'text':
          nodes = await importFromText(file);
          break;

        default:
          throw new Error(`Unsupported format: ${format}`);
      }

      // Replace editor content with imported content
      editor.tf.setValue(nodes);

    } catch (error) {
      console.error(`Error importing ${format} file:`, error);
      alert(`Failed to import ${format.toUpperCase()} file. Please check the file format and try again.`);
    }
  };



  // Create file input and trigger import
  const triggerImport = (format: string) => {
    const input = document.createElement('input');
    input.type = 'file';
    
    switch (format) {
      case 'docx':
        input.accept = '.docx';
        break;
      case 'odt':
        input.accept = '.odt';
        break;
      case 'html':
        input.accept = '.html,.htm';
        break;
      case 'markdown':
        input.accept = '.md,.markdown';
        break;
      case 'text':
        input.accept = '.txt';
        break;
    }
    
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        handleFileImport(file, format);
      }
    };
    
    input.click();
  };

  return (
    <DropdownMenu modal={false} {...openState} {...props}>
      <DropdownMenuTrigger asChild>
        <ToolbarButton pressed={openState.open} tooltip="Import Content" isDropdown>
          <ArrowUpToLineIcon className="size-4" />
        </ToolbarButton>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start">
        <DropdownMenuGroup>
          <DropdownMenuItem onSelect={() => triggerImport('docx')}>
            Import from DOCX
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => triggerImport('odt')}>
            Import from ODT
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => triggerImport('html')}>
            Import from HTML
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => triggerImport('markdown')}>
            Import from Markdown
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => triggerImport('text')}>
            Import from Text
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
